/**
 * Leitura da resposta como o cliente Python do painel antigo fazia:
 * - `r.json()`: corpo que não é JSON vira o erro do `json.loads`
 *   (ex.: "Expecting value: line 1 column 1 (char 0)");
 * - `Model.model_validate(...)`: JSON com formato errado vira o texto do
 *   `ValidationError` do Pydantic (ex.: "4 validation errors for LlmUsageEventPage ...").
 *
 * Só cobre os tipos usados pelos modelos desta página (int, float, str, datetime,
 * listas e modelos aninhados), no modo "lax" do Pydantic.
 */

/** Versão do Pydantic do painel antigo (aparece no link das mensagens). */
const VERSAO_PYDANTIC = '2.13'

/** Erro com o texto pronto para "Erro ao carregar …: {texto}". */
export class ErroLeitura extends Error {}

/** Mesma mensagem do `json.loads` quando o corpo não é JSON. */
function mensagemJsonInvalido(texto: string, posicao: number): string {
  const antes = texto.slice(0, posicao)
  const linha = antes.split('\n').length
  const coluna = posicao - antes.lastIndexOf('\n')
  return `Expecting value: line ${linha} column ${coluna} (char ${posicao})`
}

/** `r.json()`: interpreta o texto ou lança o erro do `json.loads`. */
export function lerJson(texto: string): unknown {
  try {
    return JSON.parse(texto) as unknown
  } catch {
    const posicao = texto.length - texto.replace(/^[ \t\n\r]+/, '').length
    throw new ErroLeitura(mensagemJsonInvalido(texto, posicao))
  }
}

// ---------------------------------------------------------------------------
// repr() do Python e tipo da entrada
// ---------------------------------------------------------------------------

function reprTexto(texto: string): string {
  const aspas = texto.includes("'") && !texto.includes('"') ? '"' : "'"
  let saida = ''
  for (const caractere of texto) {
    const codigo = caractere.codePointAt(0) ?? 0
    if (caractere === '\\') saida += '\\\\'
    else if (caractere === aspas) saida += `\\${aspas}`
    else if (caractere === '\n') saida += '\\n'
    else if (caractere === '\r') saida += '\\r'
    else if (caractere === '\t') saida += '\\t'
    else if (codigo < 0x20 || (codigo >= 0x7f && codigo < 0xa0)) {
      saida += `\\x${codigo.toString(16).padStart(2, '0')}`
    } else saida += caractere
  }
  return `${aspas}${saida}${aspas}`
}

function reprNumero(valor: number): string {
  if (Number.isInteger(valor) && Math.abs(valor) < 1e16) return String(valor)
  return String(valor).replace(/e([+-])(\d)$/, 'e$10$2')
}

/** `repr()` do Python para valores vindos de JSON. */
function reprPython(valor: unknown): string {
  if (valor === null || valor === undefined) return 'None'
  if (valor === true) return 'True'
  if (valor === false) return 'False'
  if (typeof valor === 'string') return reprTexto(valor)
  if (typeof valor === 'number') return reprNumero(valor)
  if (Array.isArray(valor)) return `[${valor.map(reprPython).join(', ')}]`
  if (typeof valor === 'object') {
    const itens = Object.entries(valor as Record<string, unknown>).map(
      ([chave, v]) => `${reprTexto(chave)}: ${reprPython(v)}`
    )
    return `{${itens.join(', ')}}`
  }
  return ''
}

/** `input_value` do Pydantic: o repr, cortado no meio quando passa de 50 caracteres. */
function valorDeEntrada(valor: unknown): string {
  const repr = Array.from(reprPython(valor))
  if (repr.length <= 50) return repr.join('')
  return `${repr.slice(0, 25).join('')}...${repr.slice(-24).join('')}`
}

/** `input_type` do Pydantic (nome do tipo no Python). */
function tipoPython(valor: unknown): string {
  if (valor === null || valor === undefined) return 'NoneType'
  if (typeof valor === 'boolean') return 'bool'
  if (typeof valor === 'number') return Number.isInteger(valor) ? 'int' : 'float'
  if (typeof valor === 'string') return 'str'
  if (Array.isArray(valor)) return 'list'
  return 'dict'
}

// ---------------------------------------------------------------------------
// Esquemas
// ---------------------------------------------------------------------------

interface ErroCampo {
  caminho: (string | number)[]
  tipo: string
  mensagem: string
  valor: unknown
}

export type Coletor = (valor: unknown, caminho: (string | number)[], erros: ErroCampo[]) => unknown

/** Um campo do modelo: validador e se pode faltar (tem valor padrão). */
interface Campo {
  validar: Coletor
  padrao?: () => unknown
}

const ehObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

function erro(
  erros: ErroCampo[],
  caminho: (string | number)[],
  tipo: string,
  mensagem: string,
  valor: unknown
): undefined {
  erros.push({ caminho, tipo, mensagem, valor })
  return undefined
}

/** `int` (aceita bool, float sem parte decimal e texto com número inteiro). */
export const inteiro: Coletor = (v, caminho, erros) => {
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return v
    return erro(
      erros,
      caminho,
      'int_from_float',
      'Input should be a valid integer, got a number with a fractional part',
      v
    )
  }
  if (typeof v === 'string') {
    const t = v.trim().replace(/_/g, '')
    if (/^[+-]?\d+(\.0*)?$/.test(t)) return Number(t)
    return erro(
      erros,
      caminho,
      'int_parsing',
      'Input should be a valid integer, unable to parse string as an integer',
      v
    )
  }
  return erro(erros, caminho, 'int_type', 'Input should be a valid integer', v)
}

/** `float` (aceita bool, número e texto com número). */
export const decimal: Coletor = (v, caminho, erros) => {
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'number') return v
  if (typeof v === 'string') {
    const t = v.trim()
    if (/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(t)) return Number(t)
    if (/^[+-]?(inf|infinity)$/i.test(t)) return t.startsWith('-') ? -Infinity : Infinity
    if (/^[+-]?nan$/i.test(t)) return NaN
    return erro(
      erros,
      caminho,
      'float_parsing',
      'Input should be a valid number, unable to parse string as a number',
      v
    )
  }
  return erro(erros, caminho, 'float_type', 'Input should be a valid number', v)
}

/** `str` (só aceita texto). */
export const texto: Coletor = (v, caminho, erros) => {
  if (typeof v === 'string') return v
  return erro(erros, caminho, 'string_type', 'Input should be a valid string', v)
}

const RE_DATA_HORA =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T t_](\d{2}):(\d{2})(?::(\d{2})(?:[.,]\d{1,6}\d*)?)?(?:Z|z|[+-]\d{2}(?::?\d{2})?)?)?$/

/** `datetime` (texto ISO ou número de segundos desde 1970). */
export const dataHora: Coletor = (v, caminho, erros) => {
  if (typeof v === 'number') return new Date(v * 1000)
  if (typeof v !== 'string') {
    return erro(erros, caminho, 'datetime_type', 'Input should be a valid datetime', v)
  }
  const m = RE_DATA_HORA.exec(v)
  const falha = (motivo: string) =>
    erro(
      erros,
      caminho,
      'datetime_from_date_parsing',
      `Input should be a valid datetime or date, ${motivo}`,
      v
    )
  if (!m) {
    if (!/^\d{4}/.test(v))
      return falha(v.length < 10 ? 'input is too short' : 'invalid character in year')
    if (v.length < 10) return falha('input is too short')
    return falha('unexpected extra characters at the end of the input')
  }
  const mes = Number(m[2])
  const dia = Number(m[3])
  if (mes < 1 || mes > 12) return falha('month value is outside expected range of 1-12')
  if (dia < 1 || dia > 31) return falha('day value is outside expected range')
  // Só a data: meia-noite. Sem fuso, o Python guarda a hora "ingênua" (a tabela mostra como UTC).
  if (m[4] === undefined) return new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`)
  const normal = `${v.slice(0, 10)}T${v.slice(11)}`
  const temFuso = /(Z|z|[+-]\d{2}(:?\d{2})?)$/.test(v.slice(11))
  return new Date(temFuso ? normal : `${normal}Z`)
}

/** `X | None = None`. */
export function opcional(validar: Coletor): Coletor {
  return (v, caminho, erros) => (v === null ? null : validar(v, caminho, erros))
}

/** `list[X]`. */
export function lista(validar: Coletor): Coletor {
  return (v, caminho, erros) => {
    if (!Array.isArray(v)) {
      return erro(erros, caminho, 'list_type', 'Input should be a valid list', v)
    }
    return v.map((item, i) => validar(item, [...caminho, i], erros))
  }
}

/** Modelo Pydantic com os campos na ordem da classe. */
export function modelo(nome: string, campos: Record<string, Campo>): Coletor {
  return (v, caminho, erros) => {
    if (!ehObjeto(v)) {
      return erro(
        erros,
        caminho,
        'model_type',
        `Input should be a valid dictionary or instance of ${nome}`,
        v
      )
    }
    const saida: Record<string, unknown> = {}
    for (const [chave, campo] of Object.entries(campos)) {
      if (!(chave in v)) {
        if (campo.padrao) saida[chave] = campo.padrao()
        else erro(erros, [...caminho, chave], 'missing', 'Field required', v)
        continue
      }
      saida[chave] = campo.validar(v[chave], [...caminho, chave], erros)
    }
    return saida
  }
}

/** Texto do `str(ValidationError)`. */
function textoValidacao(titulo: string, erros: ErroCampo[]): string {
  const cabecalho = `${erros.length} validation error${erros.length === 1 ? '' : 's'} for ${titulo}`
  const linhas = erros.map((e) => {
    const local = e.caminho.length ? `${e.caminho.join('.')}\n` : ''
    return (
      `${local}  ${e.mensagem} [type=${e.tipo}, input_value=${valorDeEntrada(e.valor)}, input_type=${tipoPython(e.valor)}]\n` +
      `    For further information visit https://errors.pydantic.dev/${VERSAO_PYDANTIC}/v/${e.tipo}`
    )
  })
  return [cabecalho, ...linhas].join('\n')
}

/** `model_validate`: devolve o valor convertido ou lança `ErroLeitura` com o texto do Pydantic. */
export function validar<T>(titulo: string, validador: Coletor, valor: unknown): T {
  const erros: ErroCampo[] = []
  const saida = validador(valor, [], erros)
  if (erros.length) throw new ErroLeitura(textoValidacao(titulo, erros))
  return saida as T
}
